import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import { useAuth } from '../context/AuthContext';

const LANGS = [
  { code: 'ar', label: 'ع', title: 'العربية' },
  { code: 'fa', label: 'ف', title: 'فارسی' },
  { code: 'en', label: 'EN', title: 'English' },
];

export default function Header({ serverOnline }) {
  const { lang, setLang, t, dir } = useLang();
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMenuOpen(false);
  };

  const langSwitcher = (
    <div style={{
      display: 'flex',
      background: 'rgba(255,255,255,0.06)',
      borderRadius: '50px',
      padding: '3px',
      border: '1px solid rgba(255,255,255,0.1)',
      gap: '2px',
    }}>
      {LANGS.map(({ code, label, title }) => (
        <button
          key={code}
          onClick={() => { setLang(code); setMenuOpen(false); }}
          title={title}
          style={{
            padding: '6px 12px',
            borderRadius: '50px',
            border: 'none',
            background: lang === code
              ? 'linear-gradient(135deg, #e94560, #c0392b)'
              : 'transparent',
            color: lang === code ? '#fff' : 'rgba(255,255,255,0.72)',
            fontFamily: 'Tajawal, sans-serif',
            fontSize: '13px',
            fontWeight: lang === code ? 700 : 400,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: lang === code ? '0 2px 8px rgba(233,69,96,0.4)' : 'none',
            minWidth: '36px',
            minHeight: '36px',
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );

  const authButtons = isAuthenticated ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
      <Link to="/dashboard" style={{ textDecoration: 'none' }} onClick={() => setMenuOpen(false)}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '20px',
          padding: '8px 14px',
          minHeight: '40px',
        }}>
          <span style={{ color: '#f0f0f5', fontSize: '13px', fontWeight: 600 }}>{user?.name}</span>
          {user?.plan === 'pro' && (
            <span style={{
              background: 'rgba(240,165,0,0.2)',
              color: '#f0a500',
              fontSize: '10px',
              fontWeight: 700,
              padding: '2px 7px',
              borderRadius: '10px',
              border: '1px solid rgba(240,165,0,0.3)',
            }}>PRO</span>
          )}
        </div>
      </Link>
      <button
        onClick={handleLogout}
        style={{
          padding: '8px 14px',
          background: 'rgba(233,69,96,0.12)',
          border: '1px solid rgba(233,69,96,0.3)',
          borderRadius: '20px',
          color: '#e94560',
          fontFamily: 'Tajawal, sans-serif',
          fontSize: '13px',
          fontWeight: 600,
          cursor: 'pointer',
          minHeight: '40px',
        }}
      >
        {t.nav.logout}
      </button>
    </div>
  ) : (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Link to="/login" style={{ textDecoration: 'none' }} onClick={() => setMenuOpen(false)}>
        <button style={{
          padding: '8px 18px',
          background: 'transparent',
          border: '1.5px solid rgba(78,154,241,0.4)',
          borderRadius: '20px',
          color: '#4e9af1',
          fontFamily: 'Tajawal, sans-serif',
          fontSize: '13px', fontWeight: 700,
          cursor: 'pointer', minHeight: '40px',
        }}>
          {t.nav.login}
        </button>
      </Link>
      <Link to="/register" style={{ textDecoration: 'none' }} onClick={() => setMenuOpen(false)}>
        <button style={{
          padding: '8px 18px',
          background: 'rgba(78,154,241,0.18)',
          border: '1.5px solid rgba(78,154,241,0.5)',
          borderRadius: '20px',
          color: '#4e9af1',
          fontFamily: 'Tajawal, sans-serif',
          fontSize: '13px', fontWeight: 700,
          cursor: 'pointer', minHeight: '40px',
        }}>
          {t.nav.register}
        </button>
      </Link>
    </div>
  );

  return (
    <header style={{ background: 'linear-gradient(135deg, #0f0c29, #302b63, #24243e)', boxShadow: '0 4px 24px rgba(0,0,0,0.5)', position: 'sticky', top: 0, zIndex: 100, direction: dir }}>
      {/* Main header row */}
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
        {/* Logo */}
        <Link to="/" style={{ textDecoration: 'none', flexShrink: 0 }} onClick={() => setMenuOpen(false)}>
          <h1 className="gradient-text" style={{ fontSize: '20px', fontWeight: 800, lineHeight: 1.2 }}>
            {t.appName}
          </h1>
        </Link>

        {/* Desktop nav */}
        <nav className="header-nav-desktop">
          <NavLink to="/" onClick={() => setMenuOpen(false)}>{t.nav.home}</NavLink>
          <NavLink to="/app" onClick={() => setMenuOpen(false)}>{t.nav.app}</NavLink>
          <NavLink to="/learning" onClick={() => setMenuOpen(false)}>{t.nav.learning}</NavLink>
        </nav>

        {/* Desktop right side */}
        <div className="header-right-desktop">
          {serverOnline !== undefined && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span className={`pulse-dot ${serverOnline ? 'online' : 'offline'}`} />
              <span style={{ color: serverOnline ? '#00d4aa' : '#e94560', fontSize: '12px', fontWeight: 500 }}>
                {serverOnline ? t.app.serverOnline : t.app.serverOffline}
              </span>
            </div>
          )}
          {langSwitcher}
          {authButtons}
        </div>

        {/* Hamburger — mobile only */}
        <button
          className="hamburger-btn"
          onClick={() => setMenuOpen(o => !o)}
          aria-label="Toggle menu"
          style={{ minHeight: '44px', minWidth: '44px' }}
        >
          {menuOpen ? '✕' : '☰'}
        </button>
      </div>

      {/* Mobile dropdown menu */}
      <div className={`mobile-menu${menuOpen ? ' open' : ''}`} style={{ direction: dir }}>
        {/* Nav links */}
        <Link to="/" onClick={() => setMenuOpen(false)} style={{ color: 'rgba(255,255,255,0.88)', textDecoration: 'none', fontSize: '15px', fontWeight: 600, padding: '10px 4px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>{t.nav.home}</Link>
        <Link to="/app" onClick={() => setMenuOpen(false)} style={{ color: 'rgba(255,255,255,0.88)', textDecoration: 'none', fontSize: '15px', fontWeight: 600, padding: '10px 4px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>{t.nav.app}</Link>
        <Link to="/learning" onClick={() => setMenuOpen(false)} style={{ color: 'rgba(255,255,255,0.88)', textDecoration: 'none', fontSize: '15px', fontWeight: 600, padding: '10px 4px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>{t.nav.learning}</Link>

        {/* Server status */}
        {serverOnline !== undefined && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 4px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <span className={`pulse-dot ${serverOnline ? 'online' : 'offline'}`} />
            <span style={{ color: serverOnline ? '#00d4aa' : '#e94560', fontSize: '13px', fontWeight: 500 }}>
              {serverOnline ? t.app.serverOnline : t.app.serverOffline}
            </span>
          </div>
        )}

        {/* Language switcher */}
        <div style={{ padding: '8px 4px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          {langSwitcher}
        </div>

        {/* Auth */}
        <div style={{ padding: '8px 4px' }}>
          {authButtons}
        </div>
      </div>
    </header>
  );
}

function NavLink({ to, children, onClick }) {
  return (
    <Link to={to} onClick={onClick} style={{
      color: 'rgba(255,255,255,0.78)',
      textDecoration: 'none',
      fontSize: '14px',
      fontWeight: 600,
      padding: '8px 12px',
      borderRadius: '8px',
      transition: 'all 0.2s',
      minHeight: '40px',
      display: 'flex', alignItems: 'center',
    }}
    onMouseEnter={e => { e.currentTarget.style.color = '#ffffff'; e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
    onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.78)'; e.currentTarget.style.background = 'transparent'; }}
    >
      {children}
    </Link>
  );
}
